/**
 * GlassmorphismButton - TV-optimized glassmorphism buttons
 * Matches Flutter's _TvActionButton styling from tv_detail_hero.dart
 * 
 * Variants:
 * - primary: Red gradient for main actions (Play)
 * - secondary: Gray gradient for secondary actions (Preview)
 * - icon: Circular button for like/dislike/bookmark
 * 
 * LEGACY CSS: No backdrop-blur, uses gradient backgrounds
 */
import React from 'react';
import styled from 'styled-components';
import { useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { useInputMode } from '../../utils/hooks/useInputMode';
import { FONT_FAMILY, GLOBAL_COLORS } from '../../config/globalStyles';
import { FOCUS_BORDER } from '../../config/focusStyles';

// Primary button - red glassmorphism (no blur, semi-transparent)
const PrimaryButton = styled.button`
  display: inline-block;
  vertical-align: middle;
  height: 48px;
  padding: 0 28px;
  font-family: ${FONT_FAMILY};
  font-size: 32px;
  font-weight: 600;
  color: ${props => props.$focused ? '#D32F2F' : '#FFFFFF'};
  background: ${props => props.$focused
        ? 'rgba(255, 255, 255, 0.95)'
        : 'rgba(180, 30, 30, 0.75)'};
  border-radius: 10px;
  border: 2px solid ${props => props.$focused ? '#FFF' : 'rgba(255, 255, 255, 0.25)'};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  text-shadow: ${props => props.$focused ? 'none' : '0 1px 2px rgba(0,0,0,0.8)'};
  -webkit-transition: all 0.15s ease;
  transition: all 0.15s ease;

  &:hover {
    color: #D32F2F;
    background: rgba(255, 255, 255, 0.95);
    border: 2px solid #FFF;
    text-shadow: none;
  }
`;

// Secondary button - gray glassmorphism (no blur, semi-transparent)
const SecondaryButton = styled.button`
  display: inline-block;
  vertical-align: middle;
  height: 48px;
  padding: 0 28px;
  font-family: ${FONT_FAMILY};
  font-size: 32px;
  font-weight: 600;
  color: ${props => props.$focused ? '#333' : '#FFFFFF'};
  background: ${props => props.$focused
        ? 'rgba(255, 255, 255, 0.95)'
        : 'rgba(50, 50, 50, 0.6)'};
  border-radius: 10px;
  border: 2px solid ${props => props.$focused ? '#FFF' : 'rgba(255, 255, 255, 0.2)'};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  text-shadow: ${props => props.$focused ? 'none' : '0 1px 2px rgba(0,0,0,0.8)'};
  -webkit-transition: all 0.15s ease;
  transition: all 0.15s ease;

  &:hover {
    color: #333;
    background: rgba(255, 255, 255, 0.95);
    border: 2px solid #FFF;
    text-shadow: none;
  }
`;

// Icon button - circular glassmorphism (no blur)
const IconButtonStyled = styled.button`
  display: inline-block;
  vertical-align: middle;
  width: 48px;
  height: 48px;
  padding: 0;
  font-size: 32px;
  color: ${props => props.$focused ? '#333' : '#FFFFFF'};
  background: ${props => props.$focused
        ? 'rgba(255, 255, 255, 0.95)'
        : 'rgba(50, 50, 50, 0.6)'};
  border-radius: 50%;
  border: 2px solid ${props => props.$focused ? '#FFF' : 'rgba(255, 255, 255, 0.2)'};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  margin: 0 4px;
  text-shadow: ${props => props.$focused ? 'none' : '0 1px 2px rgba(0,0,0,0.8)'};
  -webkit-transition: all 0.15s ease;
  transition: all 0.15s ease;

  &:hover {
    color: #333;
    background: rgba(255, 255, 255, 0.95);
    border: 2px solid #FFF;
    text-shadow: none;
  }
`;

// Loading spinner (pure CSS)
const Spinner = styled.span`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top-color: ${props => props.$focused ? '#D32F2F' : '#FFFFFF'};
  border-radius: 50%;
  -webkit-animation: spin 1s linear infinite;
  animation: spin 1s linear infinite;
  margin-left: 8px;

  @-webkit-keyframes spin {
    to { -webkit-transform: rotate(360deg); }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Button icon wrapper
const ButtonIcon = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-left: 8px;
  font-size: 32px;
`;

// Button label
const ButtonLabel = styled.span`
  display: inline-block;
  vertical-align: middle;
`;

/**
 * GlassmorphismButton - Primary/Secondary action button
 */
export const GlassmorphismButton = ({
    label,
    icon,
    variant = 'primary',
    loading = false,
    autofocus = false,
    onEnterPress,
    onFocus,
}) => {
    const { ref, focused, focusSelf } = useFocusable({
        onEnterPress,
        onFocus,
        focusable: !loading,
    });

    // Input mode tracking for scroll behavior
    const { isMouseFocus, setMouseFocus } = useInputMode();

    // Scroll into view when focused (skip for mouse hover)
    React.useEffect(() => {
        if (focused && ref.current && !isMouseFocus) {
            try {
                ref.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest',
                });
            } catch (e) {
                // Fallback for older browsers
                ref.current.scrollIntoView(false);
            }
        }
    }, [focused, isMouseFocus]);

    React.useEffect(() => {
        if (autofocus) {
            focusSelf();
        }
    }, [autofocus, focusSelf]);

    const ButtonComponent = variant === 'primary' ? PrimaryButton : SecondaryButton;

    const handleMouseEnter = () => {
        setMouseFocus(true);
        focusSelf();
    };

    const handleClick = () => {
        if (!loading && onEnterPress) {
            onEnterPress();
        }
    };

    return (
        <ButtonComponent
            ref={ref}
            $focused={focused}
            onMouseEnter={handleMouseEnter}
            onClick={handleClick}
            disabled={loading}
            type="button"
        >
            {loading ? (
                <Spinner $focused={focused} />
            ) : (
                <>
                    {icon && <ButtonIcon>{icon}</ButtonIcon>}
                    <ButtonLabel>{label}</ButtonLabel>
                </>
            )}
        </ButtonComponent>
    );
};

/**
 * GlassmorphismIconButton - Circular icon button for actions
 */
export const GlassmorphismIconButton = ({
    icon,
    onEnterPress,
    onFocus,
}) => {
    const { ref, focused, focusSelf } = useFocusable({
        onEnterPress,
        onFocus,
    });

    // Input mode tracking for scroll behavior
    const { isMouseFocus, setMouseFocus } = useInputMode();

    // Scroll into view when focused (skip for mouse hover)
    React.useEffect(() => {
        if (focused && ref.current && !isMouseFocus) {
            try {
                ref.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest',
                });
            } catch (e) {
                // Fallback for older browsers
                ref.current.scrollIntoView(false);
            }
        }
    }, [focused, isMouseFocus]);

    const handleMouseEnter = () => {
        setMouseFocus(true);
        focusSelf();
    };

    const handleClick = () => {
        if (onEnterPress) {
            onEnterPress();
        }
    };

    return (
        <IconButtonStyled
            ref={ref}
            $focused={focused}
            onMouseEnter={handleMouseEnter}
            onClick={handleClick}
            type="button"
        >
            {icon}
        </IconButtonStyled>
    );
};

export default GlassmorphismButton;
